import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform, useMotionTemplate } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, ShieldCheck } from "lucide-react";
import { MaskLines } from "@/components/landing/Reveal";

const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 120, damping: 18 });
  const sx = useTransform(mx, (v) => `${v * 100}%`);
  const sy = useTransform(my, (v) => `${v * 100}%`);
  const spot = useMotionTemplate`radial-gradient(420px circle at ${sx} ${sy}, rgba(245,158,11,0.0), rgba(11,15,23,0.72) 70%)`;
  const { scrollY } = useScroll();
  const py = useTransform(scrollY, [0, 800], [0, -90]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0.5); my.set(0.5); }}
      style={{ perspective: 1200, y: py }}
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
      className="relative w-full"
      data-testid="hero-visual"
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <div className="clip-frame relative aspect-[4/5] sm:aspect-[5/5] lg:aspect-[4/5] overflow-hidden bg-[#111722]">
          <img src="/img/p1.jpg" alt="Inspectores de seguridad revisando una planta industrial" className="absolute inset-0 w-full h-full object-cover grayscale-[35%] contrast-110" />
          <motion.div className="absolute inset-0" style={{ background: spot }} />
          <div className="absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-[#F59E0B]/0 via-[#F59E0B]/15 to-[#F59E0B]/0 animate-scan" />
          <div className="absolute left-4 top-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#F5F1E8]/85 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse" /> Inspección en curso
          </div>
          <div className="absolute right-4 bottom-4 font-mono text-[11px] text-[#F5F1E8]/80">PLANTA 02 · NAVE C</div>
        </div>
        <div style={{ transform: "translateZ(70px)" }} className="absolute -left-4 sm:-left-10 bottom-10 sm:bottom-16 w-[210px] sm:w-[240px] border border-white/15 bg-[#0B0F17]/80 backdrop-blur-xl p-5 clip-notch" data-testid="hero-score-card">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#F5F1E8]/65">Cumplimiento</div>
          <div className="font-display-x text-5xl font-extrabold mt-1">83<span className="text-[#F59E0B]">%</span></div>
          <div className="mt-3 h-1.5 bg-white/10 overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: "83%" }} transition={{ duration: 1.6, delay: 1.2, ease: [0.22, 1, 0.36, 1] }} className="h-full bg-emerald-400" />
          </div>
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-400 font-medium"><ShieldCheck className="w-3.5 h-3.5" /> Riesgo bajo</div>
        </div>
        <div style={{ transform: "translateZ(40px)" }} className="hidden sm:flex absolute -right-5 top-12 items-center gap-2 bg-[#F59E0B] text-[#0B0F17] px-4 py-2 font-mono text-xs font-medium uppercase tracking-[0.14em]">
          STPS + ISO 9001
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => (
  <section className="relative pt-28 sm:pt-32 pb-16 lg:pb-24 overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 grid-lines [mask-image:radial-gradient(ellipse_at_30%_30%,black,transparent_75%)]" />
    <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
      <div className="lg:col-span-7">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">
          <span className="hazard-stripe w-10 h-2.5" /> Cumplimiento STPS · Calidad ISO
        </motion.div>
        <h1 className="font-display-x mt-7 font-black uppercase leading-[0.86] tracking-[-0.03em] text-[13vw] sm:text-7xl lg:text-[6.6rem] xl:text-[7.4rem]" data-testid="hero-title">
          <MaskLines lines={["Audita.", <span key="c" className="text-stroke">Corrige.</span>, <span key="d">Cum<span className="text-[#F59E0B]">ple.</span></span>]} delay={0.15} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }} className="mt-8 max-w-xl text-base md:text-lg text-[#F5F1E8]/75 leading-relaxed">
          Vigía convierte la inspección de tu centro de trabajo en un diagnóstico claro: elige entre auditorías STPS e ISO 9001, documenta cada hallazgo con foto y obtén tu porcentaje de cumplimiento, nivel de riesgo y plan de acción al instante.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/registro" data-testid="hero-start-audit-button" className="group inline-flex items-center gap-3 bg-[#F59E0B] text-[#0B0F17] pl-7 pr-2 py-2 rounded-full font-semibold hover:bg-[#F5F1E8] transition-colors">
            Iniciar auditoría gratuita
            <span className="grid place-items-center w-10 h-10 rounded-full bg-[#0B0F17] text-[#F59E0B] transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="w-4 h-4" /></span>
          </Link>
          <a href="#plataforma" data-testid="hero-explore-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-[#F5F1E8]/80 hover:text-[#F59E0B] transition-colors">
            Ver cómo funciona <ArrowDownRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
      <div className="lg:col-span-5 px-4 sm:px-10 lg:px-0">
        <HeroVisual />
      </div>
    </div>
  </section>
);
